import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Smartphone, Users, Clock, MessageSquare, ArrowRight, Sparkles, Zap, Shield, Instagram, Star, Mail, ArrowUp } from 'lucide-react';

const XIcon = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Basit sayaç animasyonu (KPI'ler için)
const AnimatedCounter = ({ target, duration = 2000, suffix = "" }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime = null;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration]);

  return <span>{count}{suffix}</span>;
};

// KPI verileri (minimum ve anlamlı göstergeler)
const KPIS = [
  { number: 12, label: "Tester", icon: Users, color: "from-emerald-500 to-teal-600" },
  { number: 14, label: "Gün Test", icon: Clock, color: "from-teal-500 to-cyan-600" },
  { number: 100, label: "Ücretsiz", icon: Star, color: "from-emerald-600 to-green-600", suffix: "%" },
  { number: 2500, label: "Geri Bildirim", icon: MessageSquare, color: "from-amber-500 to-orange-600", suffix: "+" }
];

const Home = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const updateProgress = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const totalScrollable = scrollHeight - clientHeight;
      const progress = totalScrollable > 0 ? (scrollTop / totalScrollable) * 100 : 0;
      setScrollProgress(Math.min(100, Math.max(0, progress)));
      setShowScrollTop(scrollTop > 200);
    };
    updateProgress();
    window.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);
    return () => {
      window.removeEventListener('scroll', updateProgress);
      window.removeEventListener('resize', updateProgress);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-100 scroll-smooth">
      {/* Scroll Progress Bar */}
      <div className="fixed inset-x-0 top-0 h-1 bg-emerald-200/60 z-50">
        <div className="h-full bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 transition-[width] duration-100" style={{ width: `${scrollProgress}%` }} />
      </div>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-10 w-[28rem] h-[28rem] bg-emerald-400/25 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-10 -right-10 w-96 h-96 bg-teal-400/25 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 w-72 h-72 bg-emerald-300/15 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-white/70 backdrop-blur-sm rounded-full text-emerald-800 font-medium mb-6 border border-white/60">
              <Sparkles className="h-4 w-4 mr-2" />
              Ücretsiz Kapalı Test Platformu
            </div>
            
            <div className="flex items-center justify-center space-x-4 mb-6">
              <img 
                src="/images/favicon-96x96.png" 
                alt="12Tester Logo" 
                className="h-16 w-16 md:h-20 md:w-20"
              />
              <h1 className="text-5xl md:text-7xl font-bold bg-gradient-to-r from-emerald-700 via-teal-600 to-emerald-600 bg-clip-text text-transparent leading-tight">
                12 Tester 
              </h1>
            </div>
            
            <p className="text-xl md:text-2xl text-gray-700 mb-12 max-w-4xl mx-auto leading-relaxed">
              Mobil uygulamalarınızı Google Play Store'a hazırlamak için <span className="font-semibold text-emerald-700">12 tester</span> ve <span className="font-semibold text-teal-700"> 14 günlük </span> 
              kapalı test sürecini yönetin. Gerçek zamanlı iletişim ve kapsamlı test takibi.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
              <Link
                to="/register"
                className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-2xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2 focus-visible:ring-offset-emerald-600/0"
              >
                Hemen Başla
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center px-8 py-4 bg-white/80 backdrop-blur-sm text-gray-700 font-semibold rounded-2xl border border-white/50 hover:bg-white hover:shadow-xl transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 focus-visible:ring-offset-2"
              >
                Giriş Yap
              </Link>
            </div>
            <div id="demo-video" className="mt-8 px-0 sm:px-6">
              <div className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl bg-black">
                <div className="relative pt-[56.25%]">
                  <iframe
                    title="12Tester Demo Videosu (YouTube)"
                    src="https://www.youtube.com/embed/aVFeED-6Je8"
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Section */}
      <div className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            {KPIS.map((kpi, index) => {
              const Icon = kpi.icon;
              return (
                <div 
                  key={index}
                  className="group backdrop-blur-xl bg-white/60 rounded-2xl p-6 sm:p-8 border border-white/40 hover:shadow-2xl transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${kpi.color} rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-3xl sm:text-4xl font-bold text-gray-900 mb-1">
                    <AnimatedCounter target={kpi.number} suffix={kpi.suffix} />
                  </div>
                  <p className="text-base font-medium text-gray-600">
                    {kpi.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 sm:mb-20">
            <div className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full text-emerald-800 font-medium mb-6 border border-white/40">
              <Zap className="h-4 w-4 mr-2" />
              Platform Özellikleri
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-emerald-700 to-teal-600 bg-clip-text text-transparent mb-4 sm:mb-6">
              Neden 12 Tester?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              12Tester platformu ile uygulama test sürecinizi profesyonel bir şekilde yönetin
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
            <div className="group h-full backdrop-blur-xl bg-white/60 rounded-2xl p-6 sm:p-8 border border-white/40 hover:shadow-xl transition-transform duration-300 hover:-translate-y-1">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Smartphone className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Uygulama Yönetimi
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Uygulamalarınızı kolayca ekleyin, düzenleyin ve test süreçlerini takip edin
              </p>
            </div>

            <div className="group h-full backdrop-blur-xl bg-white/60 rounded-2xl p-6 sm:p-8 border border-white/40 hover:shadow-xl transition-transform duration-300 hover:-translate-y-1">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-500 to-green-600 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                12 Tester Sistemi
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Her uygulama için maksimum 12 tester ile kapsamlı test süreci
              </p>
            </div>

            <div className="group h-full backdrop-blur-xl bg-white/60 rounded-2xl p-6 sm:p-8 border border-white/40 hover:shadow-xl transition-transform duration-300 hover:-translate-y-1">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <Clock className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                14 Günlük Süreç
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Test sürecini 14 gün boyunca detaylı bir şekilde takip edin
              </p>
            </div>

            <div className="group h-full backdrop-blur-xl bg-white/60 rounded-2xl p-6 sm:p-8 border border-white/40 hover:shadow-xl transition-transform duration-300 hover:-translate-y-1">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-cyan-500 to-emerald-600 rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <MessageSquare className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                Gerçek Zamanlı İletişim
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Tester'lar ile anlık mesajlaşma ve geri bildirim sistemi
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/5 via-teal-600/5 to-cyan-600/5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full text-emerald-800 font-medium mb-6 border border-white/40">
              <Shield className="h-4 w-4 mr-2" />
              Basit Süreç
            </div>
            <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-6">
              Nasıl Çalışır?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              3 basit adımda uygulama test sürecinizi başlatın
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="group text-center backdrop-blur-xl bg-white/60 rounded-2xl p-8 border border-white/40 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-2xl mb-6 text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform">
                1
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Uygulama Ekleyin
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Uygulama adı, açıklama, platform ve test URL'si ile yeni bir test projesi oluşturun
              </p>
            </div>

            <div className="group text-center backdrop-blur-xl bg-white/60 rounded-2xl p-8 border border-white/40 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-2xl mb-6 text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform">
                2
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Tester'ları Bekleyin
              </h3>
              <p className="text-gray-600 leading-relaxed">
                12 tester'ın başvurusunu bekleyin ve uygun olanları onaylayın
              </p>
            </div>

            <div className="group text-center backdrop-blur-xl bg-white/60 rounded-2xl p-8 border border-white/40 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-cyan-600 to-emerald-600 text-white rounded-2xl mb-6 text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform">
                3
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Test Sürecini Takip Edin
              </h3>
              <p className="text-gray-600 leading-relaxed">
                14 gün boyunca test sürecini takip edin ve geri bildirimleri toplayın
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600"></div>
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-400/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Hemen Test Sürecinizi Başlatın
          </h2>
          <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto leading-relaxed drop-shadow-md">
            Ücretsiz hesap oluşturun ve uygulamanızı Google Play Store'a hazırlayın
          </p>
          <Link
            to="/register"
            className="group inline-flex items-center px-10 py-5 bg-white text-emerald-700 font-bold rounded-2xl hover:bg-emerald-50 hover:shadow-2xl transform hover:scale-105 transition-all duration-300 shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-emerald-700/0"
          >
            Ücretsiz Başla
            <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>

      {/* Footer Section */}
      <div className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center">
            <div className="flex items-center space-x-6 mb-8">
              <a
                href="https://x.com/12testerapp"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="12Tester'ı X üzerinden takip edin"
                className="group p-3 bg-gray-800 rounded-xl hover:bg-black transition-all duration-300"
              >
                <XIcon className="h-6 w-6 text-gray-300 group-hover:text-white" />
              </a>
              <a
                href="https://www.instagram.com/12testerapp"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="12Tester'ı Instagram üzerinden takip edin"
                className="group p-3 bg-gray-800 rounded-xl hover:bg-pink-600 transition-all duration-300"
              >
                <Instagram className="h-6 w-6 text-gray-300 group-hover:text-white" />
              </a>
              <a
                href="mailto:noreply.12tester@gmail.com"
                rel="noopener noreferrer"
                aria-label="12Tester e-posta"
                className="group p-3 bg-gray-800 rounded-xl hover:bg-emerald-600 transition-all duration-300"
              >
                <Mail className="h-6 w-6 text-gray-300 group-hover:text-white" />
              </a>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center space-x-3 mb-2">
                <img 
                  src="/images/favicon-96x96.png" 
                  alt="12Tester Logo" 
                  className="h-8 w-8"
                />
                <h3 className="text-2xl font-bold text-white">12Tester</h3>
              </div>
              <p className="text-gray-300 mb-4">Uygulama test platformu</p>
              <p className="text-sm text-gray-300">
                © 2025 12Tester. Tüm hakları saklıdır.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Scroll To Top FAB */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="En üste git"
          className="fixed bottom-6 right-6 z-50 inline-flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-transform"
        >
          <ArrowUp className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

export default Home;